import * as Sentry from '@sentry/react'
import { scrubAnalyticsUrl } from '../lib/analyticsUrl'

/** Request headers a captured failure must never carry to Sentry. */
const PRIVATE_HEADERS = /^(authorization|cookie|set-cookie|x-installation-id)$/i

function scrubHeaders(headers: Record<string, string> | undefined): void {
  if (!headers) return
  for (const name of Object.keys(headers)) {
    if (PRIVATE_HEADERS.test(name)) delete headers[name]
  }
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    // Session Replay is added after the first page is idle (below), so its
    // code is not part of the first load.
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[500, 599]],
      // The gateway (translate.itsbaba.com) serves translation, voice, chat,
      // library and email; its 5xx were invisible here. Watching a response
      // adds no request header, so no CORS preflight changes. Trace
      // propagation (tracePropagationTargets) stays off for it until the
      // Worker allows the sentry-trace and baggage headers.
      failedRequestTargets: ['https://e.itsbaba.com', 'https://translate.itsbaba.com'],
    }),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.extraErrorDataIntegration(),
  ],
  ignoreErrors: [
    /NotFoundError: Failed to execute 'insertBefore' on 'Node'/,
    /NotFoundError: The object can not be found here/,
    /\[GSI_LOGGER\]/,
    /CSSStyleSheet\.cssRules getter: Not allowed to access cross-origin stylesheet/,
    /Cannot get CSS styles from text's parentNode/,
    /One Tap exchange failed/,
    /Failed to fetch dynamically imported module/,
    /Importing a module script failed/,
    /Loading chunk \d+ failed/,
    // Third-party / browser-extension console noise (not our bundle):
    // the LocatorJS devtools extension and SES/LavaMoat (MetaMask-style
    // wallet) clipboard lockdown warnings.
    /\[locator/i,
    /CONTENT_SHELL/,
    /Clipboard\.prototype\.writeText was already locked/,
  ],
  beforeSend(event, hint) {
    // Drop benign, transient network failures (offline tab, ad-blocker, request
    // aborted on navigation). These are non-fatal in the app (callers warn and
    // continue) and only add noise. Real errors (anything not a network
    // TypeError / AbortError) pass through untouched.
    const original = hint && hint.originalException
    const first = event.exception && event.exception.values && event.exception.values[0]
    const message = (original instanceof Error ? original.message : (first && first.value)) || ''
    const type = (original instanceof Error ? original.name : (first && first.type)) || ''
    const benign = ['Failed to fetch', 'NetworkError when attempting to fetch resource', 'Load failed', 'The network connection was lost']
    let isBenignNetwork = type === 'AbortError'
    if (!isBenignNetwork && type === 'TypeError') {
      for (const t of benign) {
        if (message.includes(t)) {
          isBenignNetwork = true
          break
        }
      }
    }
    if (isBenignNetwork) return null
    // sendDefaultPii makes httpClientIntegration copy request headers and
    // cookies onto a failed-request event: the session token rides in both.
    if (event.request) {
      scrubHeaders(event.request.headers)
      delete event.request.cookies
      if (event.request.url) event.request.url = scrubAnalyticsUrl(event.request.url)
    }
    const response = event.contexts?.response as { headers?: Record<string, string>; cookies?: unknown } | undefined
    if (response) {
      scrubHeaders(response.headers)
      delete response.cookies
    }
    return event
  },
  beforeSendTransaction(event) {
    if (event.transaction) event.transaction = scrubAnalyticsUrl(event.transaction)
    if (event.request?.url) event.request.url = scrubAnalyticsUrl(event.request.url)
    return event
  },
  beforeBreadcrumb(breadcrumb) {
    const data = breadcrumb.data as Record<string, unknown> | undefined
    if (data) {
      for (const key of ['url', 'from', 'to']) {
        if (typeof data[key] === 'string') data[key] = scrubAnalyticsUrl(data[key] as string)
      }
    }
    return breadcrumb
  },
  tracesSampleRate: 0.1,
  tracePropagationTargets: ['localhost', 'https://e.itsbaba.com'],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
})

// Replay after the first page has settled: same options and sample rates as
// before (set in init above), just not on the critical path.
if (import.meta.env.PROD) {
  const addReplay = () => {
    void import('./sentryReplay')
      .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration({ maskAllInputs: true })))
      .catch(() => {})
  }
  const idleWindow = window as typeof window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
  if (typeof idleWindow.requestIdleCallback === 'function') idleWindow.requestIdleCallback(addReplay, { timeout: 5000 })
  else setTimeout(addReplay, 3000)
}
